import * as mobx from 'mobx';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as IoC from 'typescript-ioc';
import {
    ConnectionType,
    IChildConnectionDTO,
    IConnectionDetailsDTO,
    IConnectionGroupDTO,
    IConnectionOrganizationDTO,
    IConnectionsDAO,
    IDeleteConnectionRequestParams,
    IMeetingConnectionsDTO,
    onChangeConnectionEntity
} from '../../../../../api/connections.dao';
import { ConnectionVersion, SEARCH_TERM_MIN_LENGTH } from '../../../../../constants/common.constants';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { IUserOperatorDTO } from '../../../../../api/operators.dao';
import { IGNORE_ATTENDEES, IOrganizationsDAO } from '../../../../../api/organizations.dao';
import { IUsersDAO } from '../../../../../api/users.dao';
import { ILocalStorageService } from '../../../../../infrastructure/local-storage-service';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { debounceExecution } from '../../../../../infrastructure/utils/debounce.utils';
import { MeetingConnectionFields as formFields } from '../../../../../modules/connections/config/meeting-connection-config';
import { MeetingConnectionsAggregateRoot } from '../../../../../modules/connections/meeting/meeting-connections.aggregate-root';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { IProps as MeetingConnectionNotesProps } from '../../../../global/rich-text-editor';
import { ISubWindowFormMethods } from '../../manage-event.viewmodel';
import { UniqueJoiningLinkModalForm } from './unique-joining-link-modal/unique-joining-link-modal.presentation';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, CONNECTION_GROUP_TRACKING_EVENTS, OpenFrom, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { MixPanelTrackingMeetingConnectionsMapper } from './mixpanel-tracking-meeting-connections-form.mapper';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { IBlueprintsDAO } from '../../../../../api/blueprints/blueprints.dao';
import { BlueprintFieldsValues, MeetingConnectionsTemplatesVM } from './connections-templates/connections-templates.viewmodel';
import { getOrganizationsForSpeakersSync } from '../../../../../infrastructure/utils/common.utils';
import { IOrganizationsForSpeakersSync } from '../../shared/connections-list/_connection-form/_sync-speakers/sync-speakers.viewmodel';
import { IDeleteConnectionDetails } from '../../shared/connections-list/_connection-delete-confirm-popover';
import { UniqueJoiningLInksAlertsPopup } from '../../../../../infrastructure/utils/unique-joining-lInks-alerts-popup.viewmodel';
import { SendConnectionsEmailForm } from './send-connections-email-form/send-connections-email-form';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { MANAGE_PODIUM_ACCESS_TEXT, REGISTRANTS_TEXT, SEND_CONNECTIONS_EMAILS_OPTION_TEXT } from './meeting-connections-form.texts';
import { IEmailTemplateField, IEmailTemplatesDAO } from '../../../../../api/email-templates.dao';
import { EventName, IEventDispatcher } from '../../../../../infrastructure/event-dispatcher';
import { OrganizationRole } from '../../../../../modules/connections/types';
import { OrganizationMeetingConnection } from '../../../../../modules/connections/meeting/organization-meeting-connection.aggregate';
import { ChildConnectionsViewModel } from './child-connections/child-connections.viewmodel';
import { trackMixPanelPanelistDeletion } from '../../../../../infrastructure/utils/connections.utils';
import { panelistDeletionToastMessage } from '../../../../../constants/panelist-link-connection.constants';
import { transpileInlineStylesToClasses } from '../../../../global/rich-text-editor/utils/transpiler';
import { PodiumForm } from './podium-form/podium-form';
import { PodiumFormViewModel } from './podium-form/podium-form.viewmodel';
import { shouldSkipPodiumCreate } from '../../../../../constants/podium.constants';
import { ManageMeetingLinkModal } from './meeting-links/manage-meeting-link-modal/manage-meeting-link-modal';
import { MeetingLinkInheritanceIds } from './meeting-links/manage-meeting-link-modal/manage-meeting-link-modal.viewmodel';
import { MeetingLinksViewModel } from './meeting-links/meeting-links.viewmodel';
import { ADD_MEETING_LINK_MODAL_TITLE, EDIT_MEETING_LINK_MODAL_TITLE } from './meeting-links/meeting-links.texts';
import { IMeetingLinkDTO } from '../../../../../api/meeting-links.dao';

type ConnectionTypeOptionList = { id: ConnectionType; name: ConnectionType }[];

type MeetingLinkRoomInheritance = {
    presenterRoleConnectionId?: number | null;
    hostRoleConnectionId?: number | null;
    defaultConnectionId?: number | null;
    presentersConnection?: IConnectionDetailsDTO | null;
    hostsConnection?: IConnectionDetailsDTO | null;
    defaultConnection?: IConnectionDetailsDTO | null;
};
// TODO: Check this
class MeetingConnectionsFormVM {
    public static parseSearchQuery(search: string = '', searchMin = SEARCH_TERM_MIN_LENGTH): string {
        const query = search.trim();

        return query.length > searchMin ? query : '';
    }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @mobx.observable
    private meetingConnectionFormDataVM: MeetingConnectionsAggregateRoot;
    @mobx.observable
    private saving: boolean = false;
    @mobx.observable
    private isFormDataReady: boolean = false;
    @mobx.observable
    private operatorList: IUserOperatorDTO[] = [];
    @mobx.observable
    private initialValues: IMeetingConnectionsDTO;
    private operatorsQuery: string = '';
    private connectionTypeOptionList: ConnectionTypeOptionList = [];
    private participantList: IConnectionOrganizationDTO[] = [];
    private readonly eventApiParams: { eventId: number; accountId: number };
    public readonly meetingApiParams: { eventId: number; accountId: number; meetingId: number };
    private initData: IMeetingConnectionsDTO;

    @mobx.observable
    public meetingTemplateName: string = '';

    @mobx.observable
    private uniqueJoiningLinkSubWindowMap: { [index: string]: ISubWindowFacade } = {};
    @mobx.observable
    private connectionsEmailSubWindowMap: { [index: string]: ISubWindowFacade } = {};
    @mobx.observable
    private podiumPopupMap: { [index: string]: ISubWindowFacade } = {};
    @mobx.observable
    private addMeetingLinkPopupMap: { [index: string]: ISubWindowFacade } = {};
    @mobx.observable
    private editMeetingLinkPopupMap: { [index: number]: ISubWindowFacade } = {};
    @mobx.observable
    private editingMeetingLinkIds: { [index: number]: boolean } = {};
    @mobx.observable
    public updateUniqueJoiningLinkForm: () => void;
    @mobx.observable
    public connectionTemplatesVM: MeetingConnectionsTemplatesVM | null = null;
    @mobx.observable
    public closeOptionsButton: () => void;
    public initConnectionsEmailModal: () => Promise<void>;
    @mobx.observable
    public selectedEmailTemplate: IEmailTemplateField;
    @mobx.observable
    private loadingEmailTemplates = false;
    @mobx.observable
    private emailTemplatesDropdownOpened = false;
    @mobx.observable
    private emailTemplatesList: IEmailTemplateField[] = [];
    public childConnectionsVM: ChildConnectionsViewModel;
    @mobx.observable
    private podiumFormVM: PodiumFormViewModel | null = null;
    @mobx.observable
    public meetingLinksVM: MeetingLinksViewModel | null = null;

    constructor(
        private readonly accountId: number,
        private readonly eventDTO: IEventDetailsDTO,
        private readonly saveCompletedCallback: () => Promise<void>,
        private readonly meetingId: number,
        private readonly subWindowFormMethods: ISubWindowFormMethods,
        public readonly handleCloseSubWindow: (meetingId: number) => void,
        public readonly onClosePopupHandler: (callback: () => void) => void,
        public readonly handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void,
        public readonly showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void,
        public readonly trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void,
        public readonly closeMeetingConnectionPopUp: () => void,
        public readonly editConnection?: (
            meetingId: number,
            connection: IConnectionDetailsDTO,
            onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void
        ) => void,
        public readonly openFrom?: OpenFrom,
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly localeStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly emailTemplatesDAO: IEmailTemplatesDAO = IoC.Container.get(IEmailTemplatesDAO),
        @IoC.Inject private readonly eventDispatcher: IEventDispatcher = IoC.Container.get(IEventDispatcher)
    ) {
        this.saving = false;
        this.eventApiParams = { eventId: eventDTO.eventId, accountId: accountId };
        this.meetingApiParams = { ...this.eventApiParams, meetingId: meetingId };
    }

    @mobx.action
    public assignAttendeePanelistLink = (attendeeId: number, connectionId: number, skipIfExisting: boolean = false): void => {
        if (!attendeeId) return;
        const organizationsList: OrganizationMeetingConnection[] = [OrganizationRole.Presenters, OrganizationRole.Hosts, OrganizationRole.Supports]
            .map(role => this.dataVM.getOrganizationsByRole(role)).flat();
        for (const organization of organizationsList) {
            const attendee = organization.getAttendee(attendeeId);
            if (attendee) {
                if (skipIfExisting && attendee.hasAssignedConnection()) {
                    return;
                }
                attendee.assignConnection(connectionId);
                return;
            }
        };
    };

    public isSaving() { return this.saving; }
    public getIsFormDataReady() { return this.isFormDataReady; }
    public getEventTimeZone() { return this.eventDTO.timeZone; }
    public getEventName() { return this.eventDTO.name; }
    public getEventTitleForPassport() { return this.globalStorage.getEvent()?.eventTitleForPassport ?? this.eventDTO.eventTitleForPassport; }

    public get dataVM() { return this.meetingConnectionFormDataVM; }
    public get connectionTypes(): ConnectionTypeOptionList { return this.connectionTypeOptionList; }
    public get attendees() { return this.participantList; }
    public get operators() { return this.operatorList; }
    public get roomOperators() { return this.dataVM.getProps()?.room?.operators ?? []; }
    public get meetingOperators() { return this.dataVM.getOperatorList() ?? []; }
    public get event() { return this.eventApiParams; }
    public get generalMeetingId() { return this.meetingId; }
    public get meetingTopic(): string { return this.initialValues?.topic ?? ''; }
    public get isLoadingEmailTemplates(): boolean { return this.loadingEmailTemplates; }
    public get isEmailTemplatesDropdownOpened(): boolean { return this.emailTemplatesDropdownOpened; }
    public get emailTemplates(): IEmailTemplateField[] { return this.emailTemplatesList; }
    public get connectionVersion(): ConnectionVersion { return this.initData.connectionVersion; }
    public get isPodiumEnabled(): boolean { return this.podiumFormVM?.enabled ?? false; }
    public get podiumLink(): string { return this.podiumFormVM?.link ?? ''; }

    public shouldShowMeetingTemplateName(): boolean {
        return hasPermission(Permission.BLUEPRINTS_TAB) && !!this.initialValues.blueprintId;
    }

    public trackChildCopyButtonMixPanel = (eventName: string, type: string): void => {
        this.trackingService.get().track(eventName, {
            [eventName]: 'Yes',
            'Connection Type': type,
        });
    };

    public shouldShowMeetingTemplateLabel(): boolean {
        return this.shouldShowMeetingTemplateName() && !!this.meetingTemplateName;
    }

    @mobx.action
    private initMeetingTemplateName = async (): Promise<void> => {
        if (!this.shouldShowMeetingTemplateName()) return;

        try {
            const blueprints = await this.blueprintsDAO.getMeetingTemplateList({ accountId: this.accountId });
            const blueprint = blueprints.find(bp => bp.blueprintId === this.initialValues.blueprintId);

            mobx.runInAction(() => {
                if (blueprint) this.meetingTemplateName = blueprint.name;
            });
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    @mobx.action
    private initConnectionTypes = async (): Promise<void> => {
        try {
            const { types = [] } = await this.connectionsDAO.getConnectionTypes(this.eventApiParams);
            this.connectionTypeOptionList = types.map(ct => ({ id: ct, name: ct }));
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    @mobx.action
    private initParticipantsData = async (): Promise<void> => {
        try {
            const { content = [] } = await this.organizationDAO.getAllOrganizations({
                ...this.eventApiParams,
                ignoreAttendees: !IGNORE_ATTENDEES
            });
            this.participantList = content;
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    @mobx.action
    private initOperatorsData = async (query: string = ''): Promise<void> => {
        try {
            const activeOnlyUsers = { activeOnly: true };
            const { users = [] } = await this.usersDAO.searchDeliveryTeamMembers(query ? { query: query, ...activeOnlyUsers } : { ...activeOnlyUsers });
            mobx.runInAction(() => {
                this.operatorList = users.map(user =>
                ({
                    fullName: `${user.firstName} ${user.lastName}`,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    user: user
                }));
            });
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    @mobx.action
    private initMeetingConnectionsFormDataVM = async (): Promise<void> => {
        try {
            const response = await this.connectionsDAO.getMeetingConnections(this.meetingApiParams);
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            const meetingConnectionsDetails = {
                ...response,
                [formFields.MEETING_CONNECTION_NOTES]: transpileInlineStylesToClasses(response[formFields.MEETING_CONNECTION_NOTES])
            };

            this.initialValues = meetingConnectionsDetails;
            this.meetingConnectionFormDataVM = MeetingConnectionsAggregateRoot.create(meetingConnectionsDetails);
            if (response.autoSubscriptionEnabled) {
                this.meetingConnectionFormDataVM[formFields.AUTO_SUBSCRIPTION_ENABLED].setValue(true);
            }
            this.initData = JSON.parse(JSON.stringify(this.dataVM.toDTO())) as IMeetingConnectionsDTO;
        } catch (e) {
            this.handleCloseSubWindow(this.meetingApiParams.meetingId);
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    @mobx.action
    public initialize = async (): Promise<void> => {
        this.isFormDataReady = false;
        try {
            await Promise.all([
                this.initConnectionTypes,
                this.initParticipantsData,
                this.initOperatorsData,
                this.initMeetingConnectionsFormDataVM].map(async init => await init())
            );

            await this.initMeetingTemplateName();
            if (this.hasConnectionTemplatesPermissions()) {
                this.initConnectionTemplatesVM();
            }

            this.childConnectionsVM = new ChildConnectionsViewModel(this.accountId, this.eventDTO.eventId, this.meetingId, this.dataVM);
            this.podiumFormVM = new PodiumFormViewModel(this.meetingId);
            await this.podiumFormVM.initFormVM(shouldSkipPodiumCreate);
            this.meetingLinksVM = new MeetingLinksViewModel(this.meetingApiParams);
            if (this.canViewMeetingLinks()) {
                await this.meetingLinksVM.loadLinks();
            }
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isFormDataReady = true;
            });
        }
    };

    public runMeeting = () => {
        this.subWindowFormMethods.control.openEditSubWindow(this.meetingId);
    };

    public editMeeting = () => {
        this.subWindowFormMethods.schedule.openEditSubWindow(this.meetingId);
    };

    @mobx.action
    public updateDefaultConnectionId = (meetingConnectionId: number | null): void => {
        if (meetingConnectionId === null) {
            this.meetingConnectionFormDataVM.removeAssignedConnection();
        } else {
            this.meetingConnectionFormDataVM.assignConnection(meetingConnectionId);
        }
    };

    private trackEditConnectionMeetingMixPanel = (response: IMeetingConnectionsDTO) => {
        const { eventId } = this.eventApiParams;
        this.trackingService.get().track(TRACKING_EVENTS.EDIT_MEETING_CONNECTIONS, {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': eventId.toString(),
            ...MixPanelTrackingMeetingConnectionsMapper.meetingConnectionsToTrackingEvent(response)
        });
    };

    @mobx.action
    public formSubmitAction = async (connectionData?: IMeetingConnectionsDTO): Promise<boolean> => {
        if (!this.dataVM.isVmValid() || this.isSaving()) return;

        try {
            this.saving = true;
            const data = { ...(connectionData || this.dataVM.toDTO()), ...this.meetingApiParams };
            await this.connectionsDAO.updateMeetingConnections(data);
            this.onSubmitFormSuccess(data);
            return true;
        } catch (error) {
            MeetingConnectionsFormVM.handleApiException(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    public createMeetingConnection = async (connectionDetails: IConnectionDetailsDTO): Promise<void> => {
        try {
            let details = await this.connectionsDAO.createMeetingConnection({ ...connectionDetails, ...this.meetingApiParams });
            let isV2Panelist = connectionDetails.connectionVersion === ConnectionVersion.V2 && details.type === ConnectionType.PanelistLink;
            let meetingConnectionId = isV2Panelist ? Number(connectionDetails.parentMeetingConnectionId) : details.meetingConnectionId;
            let panelistInfo = null;
            if (isV2Panelist) {
                panelistInfo = details as IChildConnectionDTO;
            }
            mobx.runInAction(() => {
                this.dataVM.setMeetingConnection(details, isV2Panelist, meetingConnectionId, panelistInfo);
                if (details.type === ConnectionType.PanelistLink) {
                    if (panelistInfo) {
                        this.dataVM.setParentConnectionList(connectionDetails.parentMeetingConnectionId, panelistInfo);
                    }
                    this.assignAttendeePanelistLink(connectionDetails.attendeeId, meetingConnectionId);
                }
            });
            this.eventDispatcher.dispatch(EventName.ConnectionCreated, details, { meetingId: this.meetingId });
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    public updateMeetingConnection = async (connectionDetails: IConnectionDetailsDTO): Promise<void> => {
        try {
            const isV2PanelistChild = connectionDetails.connectionVersion === ConnectionVersion.V2 && connectionDetails.type === ConnectionType.PanelistLink;
            const details = await this.connectionsDAO.editMeetingConnection({ ...connectionDetails, ...this.meetingApiParams });
            mobx.runInAction(() => {
                this.dataVM.setMeetingConnection(details, isV2PanelistChild, connectionDetails.parentMeetingConnectionId, details as IChildConnectionDTO);
                if (isV2PanelistChild) {
                    this.dataVM.setParentConnectionList(connectionDetails.parentMeetingConnectionId, details as IChildConnectionDTO);
                }
            });
            this.eventDispatcher.dispatch(EventName.ConnectionUpdated, details, { meetingId: this.meetingId });
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    public getKnovioConnectionDetails = async (connectionDetails: IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
        return await this.connectionsDAO.getKnovioConnectionDetails({
            ...{ meetingConnectionId: connectionDetails.meetingConnectionId },
            ...this.meetingApiParams
        });
    };

    public deleteConnection = async (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails): Promise<void> => {
        const {
            meetingConnectionId,
            zoomMeetingDelete = false,
            webinarMeetingDelete = false,
            audioBridgeMeetingDelete = false,
            panelistMeetingDelete = false,
            isPanelistLink = false,
            joinMethodId
        } = data;
        try {
            const apiParams = {
                meetingConnectionId: meetingConnectionId,
                zoomMeetingDelete: zoomMeetingDelete,
                webinarMeetingDelete: webinarMeetingDelete,
                audioBridgeMeetingDelete: audioBridgeMeetingDelete,
                panelistMeetingDelete: panelistMeetingDelete,
                isPanelistLink: isPanelistLink,
                joinMethodId: joinMethodId,
                ...this.meetingApiParams
            };
            const isV2Panelist = data.connectionVersion === ConnectionVersion.V2 && isPanelistLink;
            isV2Panelist ? await this.connectionsDAO.deleteChildConnection(apiParams) : await this.connectionsDAO.deleteMeetingConnection(apiParams);
            mobx.runInAction(() => {
                isV2Panelist
                    ? this.dataVM.removeChildConnectionById(data.parentMeetingConnectionId, data.joinMethodId)
                    : this.dataVM.removeMeetingConnectionById(meetingConnectionId);
                if (isPanelistLink) {
                    trackMixPanelPanelistDeletion(TRACKING_EVENTS.DELETE_PANELIST_LINK, this.meetingApiParams.meetingId);
                    showNotification(panelistDeletionToastMessage);
                };
            });
            this.eventDispatcher.dispatch(EventName.ConnectionDeleted, { meetingConnectionId: meetingConnectionId }, { meetingId: this.meetingId });

            if (connectionDetails?.isConnectionAutoSync) {
                const uniqueJoiningLinksQuantity = this
                    .dataVM
                    .getMeetingConnectionsListByType(ConnectionType.UniqueJoiningLink)
                    .length
                    ;
                const commonAlertsPopup = new UniqueJoiningLInksAlertsPopup(
                    this.meetingId,
                    this.closeMeetingConnectionPopUp,
                    this.saveCompletedCallback,
                );
                commonAlertsPopup.handleRemoveUniqueJoiningLinks(uniqueJoiningLinksQuantity, connectionDetails.type);
            }
        }
        catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    public createMeetingConnectionGroup = async (connectionGroup: IConnectionGroupDTO): Promise<IConnectionGroupDTO> => {
        try {
            const group = await this.connectionsDAO.createMeetingConnectionGroup({ ...connectionGroup, ...this.meetingApiParams });
            mobx.runInAction(() => this.dataVM.setMeetingConnectionGroup(group));
            this.trackConnectionGroupMixpanelEvent(
                CONNECTION_GROUP_TRACKING_EVENTS.NEW_CONNECTION_GROUP,
                {
                    'Connections': MixPanelTrackingMeetingConnectionsMapper.connectionsTypeByGroup(
                        this.dataVM.getFilteredMeetingConnectionList(ConnectionType.UniqueJoiningLink),
                        connectionGroup
                    )
                }
            );
            return group;
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    public editMeetingConnectionGroup = async (connectionGroup: IConnectionGroupDTO): Promise<IConnectionGroupDTO> => {
        try {
            const group = await this.connectionsDAO.editMeetingConnectionGroup({
                groupId: connectionGroup.meetingConnectionId,
                ...connectionGroup,
                ...this.meetingApiParams
            });
            mobx.runInAction(() => this.dataVM.setMeetingConnectionGroup(group));
            this.trackConnectionGroupMixpanelEvent(
                CONNECTION_GROUP_TRACKING_EVENTS.EDIT_CONNECTION_GROUP,
                {
                    'Connection Group ID': connectionGroup.meetingConnectionId,
                    'Connections': MixPanelTrackingMeetingConnectionsMapper.connectionsTypeByGroup(
                        this.dataVM.getFilteredMeetingConnectionList(ConnectionType.UniqueJoiningLink),
                        connectionGroup
                    )
                }
            );
            return group;
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    public deleteMeetingConnectionGroup = async (groupId: number): Promise<void> => {
        try {
            await this.connectionsDAO.deleteMeetingConnectionGroup({
                groupId: groupId,
                ...this.meetingApiParams
            });
            mobx.runInAction(() => this.dataVM.removeConnectionGroupById(groupId));
            this.trackConnectionGroupMixpanelEvent(
                CONNECTION_GROUP_TRACKING_EVENTS.DELETE_CONNECTION_GROUP,
                {
                    'Connection Group ID': groupId,
                }
            );
        } catch (e) {
            MeetingConnectionsFormVM.handleApiException(e);
        }
    };

    public getMeetingConnectionNotesProps = (): MeetingConnectionNotesProps => {
        const { validationState, getValue, setValue } = this.dataVM[formFields.MEETING_CONNECTION_NOTES];

        return {
            value: getValue() as string,
            validationError: validationState.valid ? '' : <FormattedMessage {...validationState.validationMessage} />,
            onChange: (e, editor) => setValue(editor.getData())
        };
    };

    public getMeetingRecordProps = (): { isSelected: boolean; onClick: () => void } => {
        const { getValue, setValue } = this.dataVM[formFields.MEETING_RECORD_ENABLED];
        return {
            isSelected: getValue() as boolean,
            onClick: () => setValue(!getValue())
        };
    };

    public getAutoRegToggleProps = (): { isSelected: boolean; onClick: () => void } => {
        const { getValue, setValue } = this.dataVM[formFields.AUTO_REGISTRATION_ENABLED];
        return {
            isSelected: getValue() as boolean,
            onClick: () => setValue(!getValue())
        };
    };

    public getAutoSubscriptionToggleProps = (): { isSelected: boolean; onClick: () => void } => {
        const { getValue, setValue } = this.dataVM[formFields.AUTO_SUBSCRIPTION_ENABLED];
        return {
            isSelected: getValue() as boolean,
            onClick: () => setValue(!getValue())
        };
    };

    @debounceExecution(500)
    public async searchOperators(search: string = ''): Promise<void> {
        const query = MeetingConnectionsFormVM.parseSearchQuery(search);

        if (query === this.operatorsQuery) return;
        await this.initOperatorsData(query);
        this.operatorsQuery = query;
    }

    public selectOperators(operators: IUserOperatorDTO[]): void {
        const roomOperators = this.roomOperators;
        const meetingOperators = operators
            .filter(_operator => !Boolean(roomOperators.find(operator =>
                _operator.email === operator.email)))
            .reduce((_operators, operator) => {
                // eslint-disable-next-line no-unused-vars
                const { fullName, ...rest } = operator;
                return _operators.concat(rest);
            }, []);
        this.dataVM.setOperators(meetingOperators);
    }

    @mobx.action
    public openUniqueJoiningLinkModal = (
        onChangeMeetingConnection: (entity: onChangeConnectionEntity, connectionId: number | null) => void
    ): void => {
        const existingSubWindowForThisConnection = this.uniqueJoiningLinkSubWindowMap[this.meetingId];

        let shallOpenNewSubWindow = true;
        if (
            existingSubWindowForThisConnection &&
            existingSubWindowForThisConnection.getStatus() !== SubWindowStatus.Closed
        ) {
            // if we successfully maximized existing one
            existingSubWindowForThisConnection.maximize();
            shallOpenNewSubWindow = false;
        }
        if (shallOpenNewSubWindow) {

            this.uniqueJoiningLinkSubWindowMap[this.meetingId] = this.subWindowService.showSubWindow({
                title: this.intlProvider.getIntl().formatMessage(REGISTRANTS_TEXT),
                componentToHost: UniqueJoiningLinkModalForm,
                componentToHostProps: {
                    event: this.eventDTO,
                    accountId: this.accountId,
                    meetingId: this.meetingId,
                    meetingConnectionFormDataVM: this.meetingConnectionFormDataVM,
                    subWindowFormMethods: this.subWindowFormMethods,
                    onChangeMeetingConnection: onChangeMeetingConnection,
                    editConnection: this.editConnection,
                    formSubmitAction: this.formSubmitAction,
                    initData: this.initData,
                    updateUniqueJoiningLinkForm: (callback: () => void) => {
                        this.updateUniqueJoiningLinkForm = callback;
                    },
                    closeMeetingConnectionPopUp: this.closeMeetingConnectionPopUp,
                    saveCompletedCallback: this.saveCompletedCallback,
                    childConnectionsVM: this.childConnectionsVM
                },
            });
            this.onClosePopupHandler(this.uniqueJoiningLinkSubWindowMap[this.meetingId].close);
        }
    };

    @mobx.action
    public setEmailTemplatesList = (emailTemplatesList: IEmailTemplateField[]): void => {
        this.emailTemplatesList = emailTemplatesList;
    };

    @mobx.action
    public closeEmailTemplatesDropdown = (): void => {
        this.emailTemplatesDropdownOpened = false;
        this.closeOptionsButton?.();
    };

    @mobx.action
    public openEmailTemplatesDropdown = async (): Promise<void> => {
        if (this.emailTemplatesDropdownOpened) return;
        try {
            mobx.runInAction(() => {
                this.emailTemplatesDropdownOpened = true;
                this.loadingEmailTemplates = true;
            });
            const templatesList = await this.emailTemplatesDAO.getEmailTemplatesList({
                accountId: this.globalStorage.getAccount().accountId
            });
            mobx.runInAction(() => {
                this.setEmailTemplatesList(templatesList
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                );
            });
        } catch (error) {
            MeetingConnectionsFormVM.handleApiException(error);
            this.closeEmailTemplatesDropdown();
        } finally {
            mobx.runInAction(() => {
                this.loadingEmailTemplates = false;
            });
        }
    };

    @mobx.action
    public openPodiumPopup = (): void => {
        const existingSubWindowForPodiumMeeting = this.podiumPopupMap[this.meetingId];

        if (!existingSubWindowForPodiumMeeting || existingSubWindowForPodiumMeeting?.getStatus() === SubWindowStatus.Closed) {
            this.podiumPopupMap[this.meetingId] = this.subWindowService.showSubWindow({
                title: this.intlProvider.getIntl().formatMessage(MANAGE_PODIUM_ACCESS_TEXT),
                componentToHost: PodiumForm,
                componentToHostProps: {
                    meetingId: this.meetingId
                },
            });
        }
    };

    public canViewMeetingLinks = (): boolean => {
        return hasPermission(Permission.VIEW_MEETING_LINKS) || hasPermission(Permission.MANAGE_MEETING_LINKS);
    };

    public meetingLinkInheritanceIds(): MeetingLinkInheritanceIds {
        const meeting = this.dataVM.toDTO();
        const room = meeting.room as MeetingLinkRoomInheritance | undefined;
        return {
            meetingPresenterRoleConnectionId: meeting.presenterRoleConnectionId ?? null,
            meetingHostRoleConnectionId: meeting.hostRoleConnectionId ?? null,
            meetingSupportRoleConnectionId: meeting.supportRoleConnectionId ?? null,
            meetingDefaultConnectionId: meeting.defaultConnectionId ?? null,
            roomPresenterRoleConnectionId: room?.presenterRoleConnectionId ?? room?.presentersConnection?.meetingConnectionId ?? null,
            roomHostRoleConnectionId: room?.hostRoleConnectionId ?? room?.hostsConnection?.meetingConnectionId ?? null,
            roomDefaultConnectionId: room?.defaultConnectionId ?? room?.defaultConnection?.meetingConnectionId ?? null,
        };
    }

    public meetingLinkInheritanceConnections(): (IConnectionDetailsDTO | IConnectionGroupDTO)[] {
        const room = this.dataVM.toDTO().room as MeetingLinkRoomInheritance | undefined;
        return [room?.presentersConnection, room?.hostsConnection, room?.defaultConnection]
            .filter((connection): connection is IConnectionDetailsDTO => Boolean(connection?.meetingConnectionId));
    }

    @mobx.action
    public openManageMeetingLinkModal = (): void => {
        const existingSubWindow = this.addMeetingLinkPopupMap[this.meetingId];

        if (!existingSubWindow || existingSubWindow.getStatus() === SubWindowStatus.Closed) {
            this.addMeetingLinkPopupMap[this.meetingId] = this.subWindowService.showSubWindow({
                title: this.intlProvider.getIntl().formatMessage(ADD_MEETING_LINK_MODAL_TITLE),
                componentToHost: ManageMeetingLinkModal,
                componentToHostProps: {
                    meetingApiParams: this.meetingApiParams,
                    connections: this.dataVM.getMeetingConnectionList(),
                    connectionGroups: this.dataVM.getMeetingConnectionGroupList(),
                    inheritanceIds: this.meetingLinkInheritanceIds(),
                    inheritanceConnections: this.meetingLinkInheritanceConnections(),
                    onCreated: (link, connectionType) => this.meetingLinksVM?.onLinkCreated(link, connectionType),
                },
            });
        }
    };

    public isManageMeetingLinkModalShown = (): boolean => {
        return (
            this.addMeetingLinkPopupMap[this.meetingId] &&
            this.addMeetingLinkPopupMap[this.meetingId].getStatus() !== SubWindowStatus.Closed
        );
    };

    @mobx.action
    public openEditMeetingLinkModal = (link: IMeetingLinkDTO): void => {
        const existingSubWindow = this.editMeetingLinkPopupMap[link.meetingLinkId];

        if (existingSubWindow && existingSubWindow.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindow.maximize();
            return;
        }

        this.markMeetingLinkEditing(link.meetingLinkId, true);
        this.editMeetingLinkPopupMap[link.meetingLinkId] = this.subWindowService.showSubWindow({
            title: this.intlProvider.getIntl().formatMessage(EDIT_MEETING_LINK_MODAL_TITLE),
            componentToHost: ManageMeetingLinkModal,
            componentToHostProps: {
                meetingApiParams: this.meetingApiParams,
                connections: this.dataVM.getMeetingConnectionList(),
                connectionGroups: this.dataVM.getMeetingConnectionGroupList(),
                inheritanceIds: this.meetingLinkInheritanceIds(),
                inheritanceConnections: this.meetingLinkInheritanceConnections(),
                link: link,
                onUpdated: (updatedLink, connectionType) => {
                    this.meetingLinksVM?.onLinkUpdated(updatedLink, connectionType);
                    this.markMeetingLinkEditing(link.meetingLinkId, false);
                },
            },
            onCloseIconClickHandler: () => {
                this.markMeetingLinkEditing(link.meetingLinkId, false);
                this.editMeetingLinkPopupMap[link.meetingLinkId]?.close();
            },
        });
    };

    public isEditMeetingLinkModalShown = (meetingLinkId: number): boolean => {
        return Boolean(this.editingMeetingLinkIds[meetingLinkId]);
    };

    @mobx.action
    private markMeetingLinkEditing = (meetingLinkId: number, isEditing: boolean): void => {
        this.editingMeetingLinkIds[meetingLinkId] = isEditing;
    };

    public getMeetingTemplateById = async (): Promise<IEmailTemplateField> => {
        try {
            const template = await this.emailTemplatesDAO.getTemplateById(
                {
                    ...this.eventApiParams,
                    templateId: this.selectedEmailTemplate.id,
                    meetingId: this.meetingId
                }
            );
            return template;
        } catch (error) {
            MeetingConnectionsFormVM.handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.loadingEmailTemplates = false;
            });
        }
    };

    @mobx.action
    public setCurrentTemplate = (templateId: string): void => {
        this.selectedEmailTemplate = this.emailTemplates.find(t => t.id === templateId);
    };

    public selectTemplateFromList = async (templateId: string): Promise<void> => {
        const existingSubWindow = this.connectionsEmailSubWindowMap[this.meetingId];
        if (existingSubWindow && existingSubWindow.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindow.expand();
            if (templateId !== this.selectedEmailTemplate?.id) {
                this.setCurrentTemplate(templateId);
                await this.initConnectionsEmailModal();
            }
        } else {
            this.setCurrentTemplate(templateId);
            this.openConnectionsEmailModal();
        }
    };

    public trackSendConnectionEmailsEvents = (eventName: string, additionalProps?: Record<string, string | number>): void => {
        const trackingData = {
            ...this.defaultParamsForMixpanel(),
            'Template Name': this.selectedEmailTemplate.name,
            ...additionalProps
        };
        this.trackingService.get().track(eventName, trackingData);
    };

    @mobx.action
    public openConnectionsEmailModal = (): void => {
        this.connectionsEmailSubWindowMap[this.meetingId] = this.subWindowService.showSubWindow({
            title: this.intlProvider.getIntl().formatMessage(SEND_CONNECTIONS_EMAILS_OPTION_TEXT),
            isExpandedByDefault: true,
            componentToHost: SendConnectionsEmailForm,
            componentToHostProps: {
                meetingConnectionsFormVM: this,
            },
            onCloseIconClickHandler: () => {
                this.closeSendConnectionEmail();
            }
        });
    };

    public closeSendConnectionEmail = (): void => {
        this.connectionsEmailSubWindowMap[this.meetingId]?.close();
        this.setCurrentTemplate(null);
    };

    public closeUniqueJoiningLinkModal = (meetingId: number): void => {
        if (meetingId) {
            this.uniqueJoiningLinkSubWindowMap[meetingId].close();
        }
    };

    public isUniqueJoiningLinkShown = (): boolean => {
        return (
            this.uniqueJoiningLinkSubWindowMap[this.meetingId] &&
            this.uniqueJoiningLinkSubWindowMap[this.meetingId].getStatus() !== SubWindowStatus.Closed
        );
    };

    public trackEventMixPanel = (eventName: TRACKING_EVENTS): void => {
        this.trackingService.get().track(eventName, {
            'Open From': this.openFrom || OpenFrom.Connections
        });
    };

    public handleEditConnection = (connection: IConnectionDetailsDTO): void => {
        this.editConnection(this.meetingId, connection, (updatedDetailsResponse: IConnectionDetailsDTO) => {
            if (updatedDetailsResponse) {
                this.meetingConnectionFormDataVM.setMeetingConnection(updatedDetailsResponse);
            } else {
                this.meetingConnectionFormDataVM.removeMeetingConnectionById(connection.meetingConnectionId);
            }
        });
    };

    private initConnectionTemplatesVM = async (): Promise<void> => {
        this.connectionTemplatesVM = new MeetingConnectionsTemplatesVM(
            this.meetingApiParams,
            this.initialValues?.endDateTime,
            this.applyBlueprint,
            this.onCompleteGenerateConnections,
            this.connectionVersion
        );
        await this.connectionTemplatesVM.initialize();
    };

    @mobx.action
    private applyBlueprint = (fieldsValues: BlueprintFieldsValues): void => {
        if (fieldsValues.guidelines) {
            this.dataVM[formFields.MEETING_CONNECTION_NOTES].setValue(fieldsValues.guidelines as string);
        }
        this.dataVM[formFields.AUTO_REGISTRATION_ENABLED].setValue(fieldsValues.autoRegistrationEnabled as boolean);
        this.dataVM[formFields.AUTO_SUBSCRIPTION_ENABLED].setValue(fieldsValues.autoSubscriptionEnabled as boolean);
    };

    public hasConnectionTemplatesPermissions = (): boolean => {
        return hasPermission(Permission.BLUEPRINTS_TAB) && hasPermission(Permission.EDIT_MEETING_CONNECTIONS_WITH_TEMPLATE);
    };

    @mobx.action
    private onCompleteGenerateConnections = async (): Promise<void> => {
        try {
            const {
                connections,
                defaultConnectionId,
                connectionGroups,
                presenterRoleConnectionId,
                hostRoleConnectionId,
                supportRoleConnectionId
            } = await this.connectionsDAO.getMeetingConnections(this.meetingApiParams);
            const getGeneratedConnectionsCallback = this.generatedEntitiesCallback(this.dataVM.getMeetingConnectionList());
            const getGeneratedConnectionGroupsCallback = this.generatedEntitiesCallback(this.dataVM.getMeetingConnectionList());
            const generatedConnections = connections.filter(getGeneratedConnectionsCallback);
            const generatedConnectionGroups = connectionGroups.filter(getGeneratedConnectionGroupsCallback);
            const existingParentConnections = this.dataVM.getParentConnectionList();
            mobx.runInAction(() => {
                generatedConnections.forEach(c => {
                    this.dataVM.setMeetingConnection(c);
                    this.eventDispatcher.dispatch(EventName.ConnectionCreated, c, { meetingId: this.meetingId });
                    if (c.type === ConnectionType.Webinar && c.childConnections && c.childConnections.length > 0) {
                        const existingParentConnection = existingParentConnections.find(
                            parent => parent.meetingConnectionId === c.meetingConnectionId
                        );
                        c.childConnections.forEach(childConnection => {
                            if (childConnection.type === ConnectionType.PanelistLink) {
                                const childExists = existingParentConnection?.children?.some(
                                    existingChild => existingChild.joinMethodId === childConnection.joinMethodId
                                );
                                if (!childExists) {
                                    this.dataVM.setParentConnectionList(c.meetingConnectionId, childConnection);
                                    this.assignAttendeePanelistLink(childConnection.attendeeId, c.meetingConnectionId, true);
                                }
                            }
                        });
                    }
                });
                generatedConnectionGroups.forEach(c => {
                    this.dataVM.setMeetingConnectionGroup(c);
                });
                if (defaultConnectionId) {
                    this.meetingConnectionFormDataVM.assignConnection(defaultConnectionId);
                }
                this.syncMeetingRoleConnection(OrganizationRole.Presenters, presenterRoleConnectionId);
                this.syncMeetingRoleConnection(OrganizationRole.Hosts, hostRoleConnectionId);
                this.syncMeetingRoleConnection(OrganizationRole.Supports, supportRoleConnectionId);
            });

        } catch (error) {
            // no need to handle failed state
        } finally {
            this.afterGenerateConnections();
        }
    };

    private syncMeetingRoleConnection(role: OrganizationRole, connectionId?: number | null): void {
        if (connectionId) {
            this.dataVM.assignMeetingRoleConnection(role, connectionId);
            return;
        }
        if (connectionId === null) {
            this.dataVM.removeMeetingRoleConnection(role);
        }
    }

    private afterGenerateConnections = (): void => {
        this.connectionTemplatesVM.stopGeneratingConnections();
        this.connectionTemplatesVM.setIsGenerationsCompleted(true);
    };

    public getOrganizationsForSpeakersSync = (): IOrganizationsForSpeakersSync => {
        return getOrganizationsForSpeakersSync(this.initData);
    };

    private onSubmitFormSuccess = (data: IMeetingConnectionsDTO): void => {
        this.trackEditConnectionMeetingMixPanel(data);
        this.saveCompletedCallback();
        this.uniqueJoiningLinkSubWindowMap[this.meetingId]?.close();
        this.connectionsEmailSubWindowMap[this.meetingId]?.close();
    };

    private defaultParamsForMixpanel = (): Record<string, string | number> => {
        return {
            'Account ID': this.globalStorage.getAccount().accountId,
            'Account Name': this.globalStorage.getAccount().name,
            'Event ID': this.globalStorage.getEvent().eventId,
            'Event Name': this.globalStorage.getEvent().name,
            'Meeting ID': this.meetingApiParams.meetingId,
        };
    };

    private trackConnectionGroupMixpanelEvent = (eventName: string, props: Record<string, string | number>): void => {
        this.trackingService.get().track(eventName, {
            ...this.defaultParamsForMixpanel(),
            ...props
        });
    };

    private generatedEntitiesCallback(
        currentList: IConnectionDetailsDTO[] | IConnectionGroupDTO[]
    ): (
        entity: IConnectionDetailsDTO | IConnectionGroupDTO
    ) => boolean {
        return (
            entity: IConnectionDetailsDTO | IConnectionGroupDTO
        ): boolean => !currentList.some(currentListEntity => currentListEntity.meetingConnectionId === entity.meetingConnectionId);
    };
}

export { MeetingConnectionsFormVM };
